import React, { useState, useEffect, useCallback, useRef } from 'react';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { FormControl, InputLabel, Select, MenuItem, Box, Typography, CircularProgress, Tabs, Tab } from '@material-ui/core';

import { fetchUsers, fetchRoles, clearUsersAndRoles } from '../../../../../jsx/actions/paygAction';
import UsersList from '../../adminModule/AdminDashboard/UsersList';
import UserAccounts from './userAccounts';

// Motifs renvoyés par GET /superadmin/account/list (AC-31) -> libellé affiché à côté
// du compte grisé, pour que l'opérateur comprenne pourquoi il est hors d'usage.
// userAccounts.jsx (vue « Par utilisateur ») duplique cette table plutôt que de
// l'importer, pour éviter une dépendance circulaire (usersRoles.jsx importe UserAccounts).
const MANAGEABILITY_REASON_LABEL = {
    no_admin: "créateur non renseigné",
    owner_orphan: "créateur rattaché à aucun compte",
    owner_multi_account: "créateur rattaché à plusieurs comptes",
    owner_wrong_account: "créateur rattaché à un autre compte",
    admin_mismatch: "administrateur du compte incohérent",
}
const reasonLabel = (reason) => MANAGEABILITY_REASON_LABEL[reason] || reason || ""

// Bandeau d'incomplétude de la liste « Par compte » (AC-25). Le serveur signale les deux
// situations où la clé de scoping du compte n'est pas certaine : la liste rendue peut alors
// omettre des utilisateurs écrits sous l'autre clé, sans qu'aucune erreur ne le dise.
const LIST_INCOMPLETE_LABEL = {
    admin_mismatch: "Liste possiblement incomplète : la clé d'administration de ce compte est ambiguë, certains utilisateurs peuvent ne pas apparaître",
    admin_unindexed: "Liste possiblement incomplète : aucun administrateur indexé pour ce compte, certains utilisateurs peuvent ne pas apparaître",
}
const listIncompleteLabel = (reason) => LIST_INCOMPLETE_LABEL[reason] || "Liste possiblement incomplète : certains utilisateurs peuvent ne pas apparaître"

function UsersRoles(props) {
    // Bascule d'affichage (AC-U1) : « Par compte » = comportement historique inchangé,
    // « Par utilisateur » = nouvelle vue userAccounts.jsx. La liste des comptes (avec
    // manageable/reason) reste chargée dans les deux modes : le dialogue d'ajout d'accès
    // de la vue par utilisateur en a besoin sans avoir à la recharger lui-même.
    const [viewMode, setViewMode] = useState('account')
    const [accounts, setAccounts] = useState([])
    const [accountsLoading, setAccountsLoading] = useState(false)
    const [accountsError, setAccountsError] = useState("")
    const [selected, setSelected] = useState("")
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    // Motif d'incomplétude rendu par role/user/list pour le compte sélectionné, "" sinon.
    const [listIncomplete, setListIncomplete] = useState("")
    // Jeton de séquence : deux sélections rapprochées peuvent voir leurs réponses
    // arriver dans le désordre. On incrémente à chaque sélection et on ignore toute
    // réponse dont le jeton n'est plus le courant, succès comme échec, pour ne
    // jamais écraser l'état affiché par une réponse périmée (team-fix #9, point 2).
    const selectionSeq = useRef(0)

    // Tri par nom (AC-5). Le tri se refait à chaque chargement : la liste vient du
    // serveur sans ordre garanti.
    const loadAccounts = useCallback(async () => {
        setAccountsLoading(true)
        setAccountsError("")
        try {
            const req = await fetch('/superadmin/account/list', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'same-origin',
            })
            const res = await req.json()
            if (req.ok && res && Array.isArray(res.accountList)) {
                const sorted = [...res.accountList].sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                setAccounts(sorted)
            } else {
                setAccountsError((res && res.content) ? res.content : "Impossible de charger la liste des comptes")
            }
        } catch (err) {
            console.log("[ERROR] superadmin account list", err);
            setAccountsError("Erreur lors du chargement de la liste des comptes")
        }
        setAccountsLoading(false)
    }, [])

    // Montage : uniquement la liste des comptes. Aucun appel utilisateur/rôle avant
    // sélection (AC-6).
    useEffect(() => {
        loadAccounts()
    }, [loadAccounts])

    const handleSelectAccount = async (accountID) => {
        if (!accountID) {
            selectionSeq.current += 1
            setSelected("")
            setLoading(false)
            setListIncomplete("")
            return
        }
        const seq = ++selectionSeq.current
        // Purge avant tout appel réseau : sinon la table affiche transitoirement les
        // lignes du compte précédent pendant le rechargement (AC-8, R-6).
        props.clearUsersAndRoles()
        setSelected(accountID)
        setLoading(true)
        setError("")
        setListIncomplete("")

        const base = '/superadmin/account/' + accountID
        const u = await props.fetchUsers(base)
        const r = await props.fetchRoles(base)

        // Une sélection plus récente a démarré pendant l'attente : ces réponses sont
        // périmées, on ne touche plus à l'état (ni succès, ni erreur).
        if (seq !== selectionSeq.current) {
            return
        }

        if (!u.ok || !r.ok) {
            const bad = u.ok ? r : u
            setLoading(false)
            if (bad.status === 409) {
                // Refus permanent : le compte n'aurait pas dû être sélectionnable. On
                // recharge la liste pour qu'il apparaisse grisé, et on n'invite pas à
                // réessayer (AC-31, même règle que PM-7 au niveau de l'action).
                setError("Compte non exploitable : " + (bad.reason || "données incohérentes"))
                loadAccounts()
            } else {
                setError("Chargement du compte impossible, réessayez")
            }
            return
        }
        // Signalement d'incomplétude (AC-25) : la lecture est tolérée sur un compte dont la
        // clé de scoping est incertaine, mais la liste rendue peut être partielle. Sans ce
        // bandeau, un opérateur pourrait croire avoir révoqué tout le monde.
        // "" = pas de bandeau. Un motif vide alors que le drapeau est levé retombe sur
        // "unknown", donc sur le libellé générique : le bandeau ne disparaît jamais par
        // omission du motif.
        setListIncomplete(u.listPossiblyIncomplete === true ? (u.listIncompleteReason || "unknown") : "")
        setLoading(false)
    }

    return (
        <Box p={2}>
            <Typography variant="h5">Users &amp; Roles</Typography>
            <Tabs
                value={viewMode}
                onChange={(e, value) => setViewMode(value)}
                indicatorColor="primary"
                textColor="primary"
                style={{ marginBottom: 16 }}
            >
                <Tab label="Par compte" value="account" />
                <Tab label="Par utilisateur" value="user" />
            </Tabs>

            {viewMode === 'account' &&
                <>
                    <Box mt={2} mb={2} maxWidth={480}>
                        <FormControl fullWidth disabled={accountsLoading}>
                            <InputLabel id="superadmin-account-select-label">Compte</InputLabel>
                            <Select
                                labelId="superadmin-account-select-label"
                                value={selected}
                                onChange={(e) => handleSelectAccount(e.target.value)}
                            >
                                {accounts.map((acc) => (
                                    <MenuItem
                                        key={acc.accountID}
                                        value={acc.accountID}
                                        // Un compte non exploitable pour les mutations reste
                                        // SÉLECTIONNABLE quand le serveur autorise encore les
                                        // actions d'accès (seul motif admin_mismatch, AC-17).
                                        // Champ additif : absent, il vaut undefined et désactive.
                                        disabled={acc.accessActionsAllowed !== true}
                                    >
                                        {`${acc.name} — ${acc.country} — ${acc.adminEmail}`}
                                        {/* Le motif reste affiché dans les deux cas : blocage, et
                                            simple avertissement sur un compte encore actionnable. */}
                                        {acc.manageable === false ? ` (${reasonLabel(acc.reason)})` : ""}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {accountsLoading &&
                            <Box mt={1}><CircularProgress size={20} /></Box>
                        }
                        {!accountsLoading && accountsError !== "" &&
                            <Typography color="error" variant="body2">{accountsError}</Typography>
                        }
                    </Box>

                    {loading &&
                        <Box mt={1}><CircularProgress size={20} /></Box>
                    }
                    {!loading && error !== "" &&
                        <Typography color="error" variant="body2">{error}</Typography>
                    }

                    {selected !== "" && !loading && error === "" && listIncomplete !== "" &&
                        <Typography
                            variant="body2"
                            style={{ marginBottom: 8, padding: 8, backgroundColor: '#fff4e5', color: '#663c00' }}
                        >
                            {listIncompleteLabel(listIncomplete)}
                        </Typography>
                    }

                    {selected !== "" && !loading && error === "" &&
                        // suspendAllBase n'est passée QUE depuis l'onglet SuperAdmin : c'est
                        // elle qui ouvre le dialogue de choix dans UsersList. Absente du chemin
                        // admin de compte, elle garantit AC-18 par construction.
                        //
                        // mutationsAllowed suit manageable : un compte admin_mismatch reste
                        // sélectionnable et actionnable pour suspendre/réactiver/historique
                        // (accessActionsAllowed), mais le serveur refuse en 409 la création et
                        // la modification d'utilisateur ou de rôle. On désactive donc ces deux
                        // commandes plutôt que de laisser l'opérateur buter sur le refus (AC-17).
                        // Compte introuvable dans la liste : on n'autorise pas moins qu'avant,
                        // le serveur reste la garde réelle.
                        <UsersList
                            {...props}
                            apiBase={'/superadmin/account/' + selected}
                            suspendAllBase={'/superadmin/user'}
                            mutationsAllowed={(accounts.find((acc) => acc.accountID === selected) || {}).manageable !== false}
                            key={selected}
                        />
                    }
                </>
            }

            {viewMode === 'user' &&
                <UserAccounts {...props} accounts={accounts} accountsLoading={accountsLoading} />
            }
        </Box>
    );
}

function mapStateToProps(state) {
    return {
        USERS: state.users,
        ROLES: state.roles,
        pageList: state.userAccess.PAGES,
        configList: state.userAccess.CONFIG,
        // Transmis à UserAccounts (vue « Par utilisateur ») pour la règle « vous-même » ;
        // authReducer ne porte pas systématiquement l'email, userAccounts.jsx l'ignore
        // silencieusement si absent (AC-U6).
        auth: state.auth,
    }
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        fetchUsers,
        fetchRoles,
        clearUsersAndRoles,
    }, dispatch)
}

export default connect(mapStateToProps, mapDispatchToProps)(UsersRoles);
